import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { WppTypography } from '@wppopen/components-library-react'
import Tabs from 'components/Tabs/Tabs'
import appContext from 'context/appContext'
import { ClientsView } from './ClientsAndBrands'
import Banner from 'components/Banner'
import SettingsPageContext from 'context/settingsPageContext/settingsPageContext'
import RegionsTabView from './Regions/RegionsTabView'
import usePermissions from 'hooks/usePermissions'
import { BANNER_KEYS } from 'utils/constants'
import type { SettingsTabIds, TabsObject } from 'components/Tabs/tabs-type.d'
import navigationHeaderStyles from './NavigationHeader.module.scss'
import styles from './SettingsPage.module.scss'
import useInitialiser from 'hooks/useInitialiser'

const tabs: TabsObject[] = [
  { displayText: 'Clients', view: 'clients' },
  { displayText: 'Regions', view: 'regions' },
]

export default function SettingsPage() {
  const { clientsData, clientsApiMethods } = useContext(appContext)
  const { resetBanner, toastDetailsContext, activeBannerId } = useContext(SettingsPageContext)
  const navigate = useNavigate()
  const [currentTab, setCurrentTab] = useState<SettingsTabIds | ''>('')

  const { pathname } = useLocation()
  const { clientsWithPermissions } = usePermissions({
    permissionSubset: ['PDP_APP_GET_REGIONS'],
  })

  const [clientsWithGetRegions] = clientsWithPermissions

  const { isInitialising } = useInitialiser({
    subscribedApis: [clientsApiMethods],
  })

  const removeUnauthorisedTabs = useCallback(() => {
    if (!clientsWithGetRegions) return

    const canGetRegions = !!clientsWithGetRegions?.length
    if (!canGetRegions) {
      const regionTabIdx = tabs.findIndex(tab => tab.view === 'regions')
      if (regionTabIdx > -1) tabs.splice(regionTabIdx, 1)
    }
  }, [clientsWithGetRegions])

  useLayoutEffect(() => {
    removeUnauthorisedTabs()
  }, [clientsWithGetRegions])

  // set current tab based on URL
  useLayoutEffect(() => {
    if (!tabs.length) {
      setCurrentTab('')
      return
    }
    // get all tabs
    const tabsViews = tabs.map(tab => tab.view)
    // get current tab from URL
    const urlTab = pathname.split('/').pop() as SettingsTabIds
    // check if current tab is in "tabsViews"
    if (urlTab && tabsViews.includes(urlTab)) {
      setCurrentTab(urlTab)
      return
    }

    setCurrentTab(tabs[0]?.view as SettingsTabIds)
  }, [tabs])

  // update url when current tab changes
  useEffect(() => {
    if (currentTab) {
      const updateCurrentTab = currentTab === 'clients' ? 'clients' : currentTab
      navigate(`/settings/${updateCurrentTab}`)
    }
  }, [currentTab, navigate])

  const tabView = useMemo(() => {
    switch (currentTab) {
      case 'clients': {
        return <ClientsView clientsData={clientsData} />
      }
      case 'regions': {
        return <RegionsTabView />
      }
      default: {
        return null
      }
    }
  }, [currentTab, clientsData])

  const tabsElement = useMemo(() => {
    return (
      <Tabs
        isInitialising={isInitialising}
        currentTab={currentTab}
        dataKeys={tabs}
        onSetNewTab={setCurrentTab as Dispatch<SetStateAction<string>>}
        align="left"
      />
    )
  }, [currentTab, tabs, isInitialising])

  return (
    <div className="dark-bg-page">
      <div className={navigationHeaderStyles.navigationHeader__container}>
        <Banner
          text={toastDetailsContext?.message}
          type={toastDetailsContext?.type}
          shouldBannerShow={activeBannerId === BANNER_KEYS.SETTINGS_HOME && !!toastDetailsContext}
          onClose={resetBanner}
        />
      </div>

      <section className={styles.settings_container}>
        <WppTypography tag="h1" type="3xl-heading">
          Settings
        </WppTypography>

        {/* tabs */}
        {tabsElement}

        {/* clients */}
        {tabView}
      </section>
    </div>
  )
}
